import { useMutation, useQueryClient } from '@tanstack/react-query'

import { patchRequest } from 'src/lib/api-client'
import { queries } from 'src/lib/query-keys'
import type { LeadDistributionPreference } from 'src/shared/features/enterprise/api/use-update-enterprise-settings'

import type { EnterpriseDistributionSystemResponse } from './use-lead-distributions'

export interface UpdateEnterpriseDistributionSystemParams {
  enterpriseId: number
  distributionSystemId: number
  name?: string
  distributionType?: LeadDistributionPreference
  memberUserIds?: number[]
}

const buildUpdateBody = (params: UpdateEnterpriseDistributionSystemParams) => {
  const body: {
    name?: string
    distribution_type?: LeadDistributionPreference
    member_user_ids?: number[]
  } = {}
  if (params.name !== undefined) {
    body.name = params.name
  }
  if (params.distributionType !== undefined) {
    body.distribution_type = params.distributionType
  }
  if (params.memberUserIds !== undefined) {
    body.member_user_ids = params.memberUserIds
  }
  return body
}

const updateEnterpriseDistributionSystem = async (params: UpdateEnterpriseDistributionSystemParams) => {
  const { enterpriseId, distributionSystemId } = params
  return patchRequest<EnterpriseDistributionSystemResponse>(
    `/enterprise/${enterpriseId}/distribution-systems/${distributionSystemId}`,
    buildUpdateBody(params),
  )
}

export default function useEditLeadDistribution() {
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: async (params: UpdateEnterpriseDistributionSystemParams) => {
      const response = await updateEnterpriseDistributionSystem(params)

      if (!response) {
        throw new Error('No response received from update distribution system')
      }

      queryClient.invalidateQueries({ queryKey: queries.enterprise.distributionSystems._def })

      return response
    },
  })
}
